import { useState, useEffect, useRef } from "react";
import { Star, BadgeCheck, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ActionButton from "./ui/ActionButton";
import illustrationSniffingDog from "@/assets/illustration-sniffing-dog.webp";
import { serviceLabels } from "@/data/serviceLabels";

interface Caregiver {
  id: string;
  name: string;
  location: string;
  rating: number;
  reviews: number;
  bio: string;
  services: string[];
  priceFrom: number;
  verified: boolean;
  featured: boolean;
  photo: string;
}

const TopCaregivers = () => {
  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    if (loading) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [loading]);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener("scroll", checkScroll);
    return () => { if (el) el.removeEventListener("scroll", checkScroll); };
  }, [caregivers]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector("div")?.offsetWidth || 300;
    el.scrollBy({ left: dir === "left" ? -cardWidth - 24 : cardWidth + 24, behavior: "smooth" });
  };

  useEffect(() => {
    const fetchTopCaregivers = async () => {
      try {
        const { data, error } = await supabase
          .from("caregiver_details")
          .select(`
            id, rating, review_count, is_verified, is_featured,
            profiles:public_caregiver_profiles!inner ( id, full_name, avatar_url, address_city, bio ),
            caregiver_services ( service_type, price )
          `)
          .eq("is_approved", true)
          .eq("is_visible", true)
          .order("rating", { ascending: false })
          .order("review_count", { ascending: false })
          .limit(8);

        if (error) throw error;

        const formatted: Caregiver[] = (data || []).map((c: any) => {
          const services = [...new Set((c.caregiver_services || []).map((s: any) => serviceLabels[s.service_type] || s.service_type))].slice(0, 3) as string[];
          const minPrice = c.caregiver_services?.length ? Math.min(...c.caregiver_services.map((s: any) => parseFloat(s.price))) : 0;

          return {
            id: c.id,
            name: c.profiles?.full_name || "Cuidador",
            location: c.profiles?.address_city || "San Salvador",
            rating: parseFloat(c.rating) || 0,
            reviews: c.review_count || 0,
            bio: c.profiles?.bio || "Cuidador profesional",
            services,
            priceFrom: minPrice,
            verified: c.is_verified || false,
            featured: c.is_featured || false,
            photo: c.profiles?.avatar_url || "",
          };
        });

        setCaregivers(formatted);
      } catch (err) {
        console.error("Error fetching top caregivers:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTopCaregivers();
  }, []);

  const getInitial = (name: string) => name.charAt(0).toUpperCase();

  if (loading) {
    return (
      <section id="cuidadores" className="section-padding" style={{ backgroundColor: "#eef4e9" }}>
        <div className="container-custom mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-3xl font-fredoka font-bold text-foreground mb-3 md:text-6xl">Cuidadores destacados cerca de ti</h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto md:text-2xl">Todos verificados · Con reseñas de dueños reales</p>
          </div>
          <div className="relative">
            <div
              className="flex gap-6 overflow-x-auto overflow-y-visible scrollbar-hide snap-x snap-mandatory pb-6 pt-2 -mx-4 px-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {[
                { name: "Cargando...", location: "San Salvador", services: ["Paseo", "Hospedaje"], price: 15 },
                { name: "Cargando...", location: "Santa Tecla", services: ["Guardería", "Paseo"], price: 20 },
                { name: "Cargando...", location: "Antiguo Cuscatlán", services: ["Hospedaje"], price: 18 },
                { name: "Cargando...", location: "San Salvador", services: ["Paseo", "Guardería"], price: 12 },
              ].map((ph, i) => (
                <div
                  key={i}
                  className="bg-card rounded-2xl shadow-card p-5 transition-all duration-500 group relative snap-start shrink-0 w-[calc(100%-8px)] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)] border border-secondary-foreground animate-pulse"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="relative shrink-0">
                      <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center border-[3px] border-primary/30">
                        <span className="font-fredoka text-2xl text-primary">{getInitial(ph.name)}</span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-fredoka text-base font-semibold text-foreground truncate">{ph.name}</h3>
                      <p className="font-poppins text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin size={12} /> {ph.location}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 mb-3">
                    <Star className="w-4 h-4 fill-accent text-accent" />
                    <span className="font-fredoka text-sm">Nuevo</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {ph.services.map((s, j) => (
                      <span key={j} className="badge-service text-[11px]">{s}</span>
                    ))}
                  </div>

                  <p className="font-poppins text-sm text-muted-foreground mb-4">
                    Desde <span className="font-fredoka text-xl text-primary font-bold">${ph.price}</span> / servicio
                  </p>

                  <ActionButton to="#" theme="outlineAccent" size="sm" className="w-full">
                    Ver perfil
                  </ActionButton>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-10">
            <ActionButton to="/buscar" size="lg">
              Ver todos los cuidadores →
            </ActionButton>
          </div>
        </div>
      </section>
    );
  }

  if (caregivers.length === 0) {
    return (
      <section id="cuidadores" className="section-padding" style={{ backgroundColor: "#eef4e9" }}>
        <div className="container-custom mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <h2 className="text-3xl font-fredoka font-bold text-foreground mb-3 md:text-6xl">Cuidadores destacados cerca de ti</h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto md:text-2xl">Todos verificados · Con reseñas de dueños reales</p>
          </div>
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <img src={illustrationSniffingDog} alt="Pronto llegarán nuestros Compawdres" width={160} height={160} loading="lazy" className="w-40 h-40 object-contain mb-6" />
            <p className="font-fredoka text-xl md:text-2xl text-foreground text-center mb-2">
              Muy pronto podrás conocer a nuestros Compawdres destacados.
            </p>
            <p className="font-poppins text-base text-muted-foreground text-center">¡Estamos comenzando! 🐾</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} id="cuidadores" className="section-padding" style={{ backgroundColor: "#eef4e9" }}>
      <div className="container-custom mx-auto">
        <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="text-3xl font-fredoka font-bold text-foreground mb-3 md:text-6xl">
            Cuidadores destacados cerca de ti
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto md:text-2xl">
            Todos verificados · Con reseñas de dueños reales
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={() => scroll("left")}
              className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-card shadow-lg rounded-full p-2 hover:bg-muted transition-colors hidden md:flex items-center justify-center"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5 text-foreground" />
            </button>
          )}

          {/* Right arrow */}
          {canScrollRight && (
            <button
              onClick={() => scroll("right")}
              className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-card shadow-lg rounded-full p-2 hover:bg-muted transition-colors hidden md:flex items-center justify-center"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-5 h-5 text-foreground" />
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-auto overflow-y-visible scrollbar-hide snap-x snap-mandatory pb-6 pt-2 -mx-4 px-4"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {caregivers.map((cg, index) => (
              <div
                key={cg.id}
                className={`bg-card rounded-2xl shadow-card p-5 hover:shadow-hover hover:-translate-y-1 transition-all duration-500 cursor-pointer group relative snap-start shrink-0 w-[calc(100%-8px)] sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(25%-18px)] border border-secondary-foreground ${
                  visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: visible ? `${index * 100}ms` : "0ms" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative shrink-0">
                    {cg.photo ? (
                      <img src={cg.photo} alt={cg.name} width={72} height={72} loading="lazy" className="w-[72px] h-[72px] rounded-full object-cover border-[3px] border-primary/30" />
                    ) : (
                      <div className="w-[72px] h-[72px] rounded-full bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center border-[3px] border-primary/30">
                        <span className="font-fredoka text-2xl text-primary">{getInitial(cg.name)}</span>
                      </div>
                    )}
                    {cg.verified && (
                      <div className="absolute -top-1 -right-1 bg-secondary text-secondary-foreground rounded-full px-1.5 py-0.5 text-[10px] font-fredoka font-semibold flex items-center gap-0.5 shadow-sm">
                        <BadgeCheck size={10} /> ✓
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-fredoka text-base font-semibold text-foreground truncate">{cg.name}</h3>
                    <p className="font-poppins text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin size={12} /> {cg.location}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1 mb-3">
                  <Star className="w-4 h-4 fill-accent text-accent" />
                  <span className="font-fredoka text-sm">{cg.rating > 0 ? cg.rating.toFixed(1) : "Nuevo"}</span>
                  {cg.reviews > 0 && (
                    <span className="font-poppins text-xs text-muted-foreground">· ({cg.reviews} reseñas)</span>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {cg.services.map((s, i) => (
                    <span key={i} className="badge-service text-[11px]">{s}</span>
                  ))}
                </div>

                <p className="font-poppins text-sm text-muted-foreground mb-4">
                  Desde <span className="font-fredoka text-xl text-primary font-bold">${cg.priceFrom}</span> / servicio
                </p>

                <ActionButton to={`/compawdre/${cg.id}`} theme="outlineAccent" size="sm" className="w-full">
                  Ver perfil
                </ActionButton>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <ActionButton to="/buscar" size="lg">
            Ver todos los cuidadores →
          </ActionButton>
        </div>
      </div>
    </section>
  );
};

export default TopCaregivers;
